import { Instagram, Linkedin, Twitter, Facebook, Mail, MapPin, Phone } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (isHomePage) {
      // On homepage, just scroll to the section
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // On other pages, navigate to homepage with hash
      navigate('/' + href);
    }
  };

  const socialLinks = [{
    icon: Instagram,
    href: 'https://www.instagram.com/rakibhossentopu/',
    label: 'Instagram'
  }, {
    icon: Linkedin,
    href: 'https://www.linkedin.com/in/rakibhossentopu/',
    label: 'LinkedIn'
  }, {
    icon: Twitter,
    href: 'https://x.com/rakibhassantopu',
    label: 'Twitter'
  }, {
    icon: Facebook,
    href: 'https://www.facebook.com/rakibhossentopu',
    label: 'Facebook'
  }];
  const quickLinks = [{
    label: 'About',
    href: '#about'
  }, {
    label: 'Services',
    href: '#services'
  }, {
    label: 'Portfolio',
    href: '#portfolio'
  }, {
    label: 'Blog',
    href: '/blog',
    isExternal: true
  }, {
    label: 'Contact',
    href: '#contact'
  }];
  const services = [{
    label: 'Search Engine Optimization',
    href: '#services'
  }, {
    label: 'Local SEO',
    href: '#services'
  }, {
    label: 'YouTube SEO',
    href: '#services'
  }, {
    label: 'Meta Ads',
    href: '#services'
  }, {
    label: 'Digital Marketing',
    href: '#services'
  }, {
    label: 'Business Strategy',
    href: '#services'
  }];
  return (
    <footer className="bg-gradient-to-b from-foreground to-foreground/95 text-background py-16">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <a href="#home" onClick={(e) => handleNavClick(e, '#home')} className="text-3xl font-bold inline-block hover:text-primary transition-colors">
              Rakib Hossen Topu
            </a>
            <p className="text-background/80 text-sm leading-relaxed">
              <strong>SEO Expert in BD</strong> - Professional SEO specialist and digital marketing expert based in Dhaka, Bangladesh. Helping businesses achieve top Google rankings and sustainable growth through proven SEO strategies.
            </p>
            <div className="flex space-x-4 pt-2">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-lg bg-background/10 hover:bg-primary flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-background">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.isExternal ? (
                    <a
                      href={link.href}
                      className="text-background/80 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="text-background/80 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-background">Services</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href={service.href}
                    onClick={(e) => handleNavClick(e, service.href)}
                    className="text-background/80 hover:text-primary hover:translate-x-1 inline-block transition-all duration-200 text-sm"
                  >
                    {service.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-background">Get In Touch</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-background/80">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary" />
                <a
                  href="#contact"
                  onClick={(e) => handleNavClick(e, '#contact')}
                  className="hover:text-primary transition-colors text-sm"
                >
                  Contact via form
                </a>
              </li>
              <li className="flex items-start gap-3 text-background/80 text-sm">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary" />
                <span>Available for projects worldwide</span>
              </li>
              <li className="flex items-start gap-3 text-background/80 text-sm">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary" />
                <span>Available for consultation</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-background/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-background/70">
            <p>
              © {currentYear} Rakib Hassan Topu. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="/sitemap.xml" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Sitemap
              </a>
              <span>•</span>
              <p className="flex items-center gap-1">
                Crafted with expertise in SEO & Digital Marketing
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
